import * as React from "react";
import { cx } from "../util.js";
import type { CodexType } from "./Chip.js";

export interface EntityMentionProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Codex type, drives the underline color. */
  type?: CodexType;
  /** Codex entry id this mention links to (for click-through). */
  entryId?: string;
}
/**
 * An inline manuscript mention of a codex entry: dotted underline in the
 * type color, boxes on hover, clickable to open the entry.
 */
export function EntityMention({ type = "character", entryId, className, children, onKeyDown, ...rest }: EntityMentionProps) {
  return (
    <span
      className={cx("ip-ent", className)}
      data-type={type}
      data-entry-id={entryId}
      role="link"
      tabIndex={0}
      {...rest}
      onKeyDown={event => {
        onKeyDown?.(event);
        if (!event.defaultPrevented && event.target === event.currentTarget && event.key === "Enter" && !event.repeat) {
          event.preventDefault(); event.currentTarget.click();
        }
      }}
    >
      {children}
    </span>
  );
}

export interface ProgressionBlockProps extends React.DetailsHTMLAttributes<HTMLDetailsElement> {
  /** Detail field being changed, e.g. "Quirks". */
  detail: string;
  /** Target entry name, e.g. "Maria Hale". */
  entry: string;
  /** The change text, e.g. "now has driving anxiety". */
  change: React.ReactNode;
  /** Whether this adds to or replaces the detail. @default "add" */
  mode?: "add" | "replace";
}
/**
 * Inline, scene-anchored codex progression marker. Represents a time-bound
 * change applied to a codex entry's detail from this scene forward.
 */
export function ProgressionBlock({ detail, entry, change, mode = "add", className, ...rest }: ProgressionBlockProps) {
  return (
    <details className={cx("ip-progression", className)} {...rest}>
      <summary><b>{entry}</b> · {detail} change</summary>
      <div className="ip-progression-detail">
        <span>{mode === "replace" ? "Replace" : "Add to"} {detail}: {change}</span>
        <span className="ip-progression-from">From this scene onward</span>
      </div>
    </details>
  );
}
